// Work-hours time helpers. All times locked to 9:00 AM – 7:00 PM.
// Display format: "HH:MM AM/PM" (e.g. "09:30 AM"). Storage format unchanged: "HH:MM:SS" 24h.

export const WORK_START_HOUR = 9; // 9 AM
export const WORK_END_HOUR = 19; // 7 PM (inclusive)
export const MINUTE_STEP = 5;

export const HOUR_OPTIONS = [9, 10, 11, 12, 1, 2, 3, 4, 5, 6, 7] as const;
export const MINUTE_OPTIONS = Array.from({ length: 12 }, (_, i) => i * 5);

export type Period = "AM" | "PM";

/** Parse "HH:MM AM/PM" or 24h "HH:MM" / "HH:MM:SS" into {hour24, minute}. */
export function parseTime(value: string | null | undefined): { hour24: number; minute: number } | null {
  if (!value) return null;
  const v = value.trim().toUpperCase();
  const ampm = v.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/);
  if (ampm) {
    let h = parseInt(ampm[1], 10);
    const m = parseInt(ampm[2], 10);
    const p = ampm[3] as Period;
    if (p === "PM" && h !== 12) h += 12;
    if (p === "AM" && h === 12) h = 0;
    return { hour24: h, minute: m };
  }
  const h24 = v.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (h24) return { hour24: parseInt(h24[1], 10), minute: parseInt(h24[2], 10) };
  return null;
}

export function to12h(hour24: number): { hour12: number; period: Period } {
  const period: Period = hour24 >= 12 ? "PM" : "AM";
  let h = hour24 % 12;
  if (h === 0) h = 12;
  return { hour12: h, period };
}

export function to24h(hour12: number, period: Period): number {
  if (period === "AM") return hour12 === 12 ? 0 : hour12;
  return hour12 === 12 ? 12 : hour12 + 12;
}

export function formatDisplay(value: string | null | undefined): string {
  const p = parseTime(value);
  if (!p) return "";
  const { hour12, period } = to12h(p.hour24);
  return `${String(hour12).padStart(2, "0")}:${String(p.minute).padStart(2, "0")} ${period}`;
}

/** Convert "HH:MM AM/PM" or 24h to canonical "HH:MM:SS" for DB storage. */
export function toStorage(value: string | null | undefined): string | null {
  const p = parseTime(value);
  if (!p) return null;
  return `${String(p.hour24).padStart(2, "0")}:${String(p.minute).padStart(2, "0")}:00`;
}

export function isWithinWorkHours(value: string): boolean {
  const p = parseTime(value);
  if (!p) return false;
  const totalMin = p.hour24 * 60 + p.minute;
  return totalMin >= WORK_START_HOUR * 60 && totalMin <= WORK_END_HOUR * 60;
}

export function compareTimes(a: string, b: string): number {
  const pa = parseTime(a);
  const pb = parseTime(b);
  if (!pa || !pb) return 0;
  return pa.hour24 * 60 + pa.minute - (pb.hour24 * 60 + pb.minute);
}

export function diffMinutes(start: string, end: string): number {
  const s = parseTime(start);
  const e = parseTime(end);
  if (!s || !e) return 0;
  return e.hour24 * 60 + e.minute - (s.hour24 * 60 + s.minute);
}

export function formatDurationShort(mins: number): string {
  if (!mins || mins <= 0) return "";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}

/** Format a stored time-range for table cells: "09:30 AM – 11:00 AM". */
export function formatRange(start: string | null | undefined, end: string | null | undefined): string {
  const s = formatDisplay(start);
  const e = formatDisplay(end);
  if (!s || !e) return "—";
  return `${s} – ${e}`;
}

/** Default Start = now (IST) rounded to nearest 5-min, clamped to work hours. */
export function defaultStartTime(): string {
  // Read wall-clock hours/minutes in IST regardless of browser timezone.
  const istHHmm = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date());
  const [hStr, mStr] = istHHmm.split(":");
  let h = parseInt(hStr, 10);
  let m = Math.round(parseInt(mStr, 10) / MINUTE_STEP) * MINUTE_STEP;
  if (m === 60) { m = 0; h += 1; }
  if (h < WORK_START_HOUR) { h = WORK_START_HOUR; m = 0; }
  if (h > WORK_END_HOUR || (h === WORK_END_HOUR && m > 0)) { h = WORK_START_HOUR; m = 0; }
  const { hour12, period } = to12h(h);
  return `${String(hour12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${period}`;
}

export function defaultEndTime(start: string): string {
  const p = parseTime(start);
  if (!p) return defaultStartTime();
  let totalMin = p.hour24 * 60 + p.minute + 30;
  const max = WORK_END_HOUR * 60;
  if (totalMin > max) totalMin = max;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  const { hour12, period } = to12h(h);
  return `${String(hour12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${period}`;
}
